'use client';

import { useEffect, useRef, type RefObject } from 'react';

interface UseSectionDwellOptions {
  /** Minimum intersection ratio (0–1) before the dwell timer starts. */
  threshold?: number;
  /** How long the section must stay visible before firing (ms). */
  dwellMs?: number;
  /** Called each time the dwell threshold is met. */
  onDwell: () => void;
  /** When false, the observer is not attached. */
  enabled?: boolean;
}

/**
 * Fires `onDwell` after the observed element stays sufficiently visible
 * for `dwellMs` consecutive milliseconds. The timer resets when the user
 * scrolls away, and can fire again on the next dwell.
 */
export function useSectionDwell(
  ref: RefObject<HTMLElement | null>,
  { threshold = 0.35, dwellMs = 5000, onDwell, enabled = true }: UseSectionDwellOptions,
) {
  const onDwellRef = useRef(onDwell);

  useEffect(() => {
    onDwellRef.current = onDwell;
  }, [onDwell]);

  useEffect(() => {
    if (!enabled) return;

    const element = ref.current;
    if (!element) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const trigger = () => {
      clearTimer();
      onDwellRef.current();
    };

    if (!('IntersectionObserver' in window)) {
      timer = setTimeout(trigger, dwellMs);
      return () => clearTimer();
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          if (!timer) {
            timer = setTimeout(trigger, dwellMs);
          }
        } else {
          clearTimer();
        }
      },
      { threshold: [0, threshold, 0.5, 0.75, 1] },
    );

    observer.observe(element);
    return () => {
      clearTimer();
      observer.disconnect();
    };
  }, [dwellMs, enabled, ref, threshold]);
}
